/**
 * Retry Logic Utility for Firebase Operations
 * Implements exponential backoff with jitter for better resilience
 */

class RetryLogic {
  constructor(options = {}) {
    this.maxRetries = options.maxRetries || 3;
    this.baseDelay = options.baseDelay || 1000; // 1 second
    this.maxDelay = options.maxDelay || 30000; // 30 seconds
    this.backoffFactor = options.backoffFactor || 2;
    this.retryableErrors = options.retryableErrors || [
      'unavailable',
      'deadline-exceeded',
      'resource-exhausted',
      'internal',
      'unknown',
      'UNAVAILABLE',
      'DEADLINE_EXCEEDED',
      'RESOURCE_EXHAUSTED',
      'INTERNAL',
      'UNKNOWN',
      'network-request-failed',
      'ERR_NETWORK',
      'FETCH_ERROR',
      'timeout',
      'TIMEOUT'
    ];
  }

  /**
   * Check if an error is retryable
   * @param {Error} error - The error to check
   * @returns {boolean} Whether the error is retryable
   */
  isRetryableError(error) {
    if (!error) return false;

    const errorMessage = error.message || '';
    const errorCode = error.code || '';
    const errorName = error.name || '';

    return this.retryableErrors.some(pattern =>
      errorMessage.toLowerCase().includes(pattern.toLowerCase()) ||
      errorCode.toLowerCase().includes(pattern.toLowerCase()) ||
      errorName.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  /**
   * Calculate delay for exponential backoff with jitter
   * @param {number} attempt - Current attempt number (0-based)
   * @returns {number} Delay in milliseconds
   */
  calculateDelay(attempt) {
    // Exponential backoff
    const exponentialDelay = this.baseDelay * Math.pow(this.backoffFactor, attempt);

    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 0.1 * exponentialDelay;

    // Cap at max delay
    return Math.min(exponentialDelay + jitter, this.maxDelay);
  }

  /**
   * Sleep for a given amount of time
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise} Promise that resolves after the delay
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Execute a function with retry logic
   * @param {Function} fn - Function to execute
   * @param {Object} options - Override options for this specific retry
   * @returns {Promise} Promise that resolves with the function result
   */
  async execute(fn, options = {}) {
    const config = { ...this, ...options };
    let lastError;

    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
      try {
        const result = await fn();

        // Log successful retry attempts
        if (attempt > 0) {
          console.log(`✅ Operation succeeded after ${attempt} retries`);
        }

        return result;
      } catch (error) {
        lastError = error;

        // Don't retry if this is the last attempt or error is not retryable
        if (attempt === config.maxRetries || !this.isRetryableError(error)) {
          break;
        }

        const delay = this.calculateDelay(attempt);
        console.warn(`⚠️ Retry ${attempt + 1}/${config.maxRetries} after ${delay}ms. Error: ${error.message}`);

        await this.sleep(delay);
      }
    }

    // All retries exhausted
    console.error(`❌ Operation failed after ${config.maxRetries} retries. Final error:`, lastError);
    throw lastError;
  }

  /**
   * Create a wrapped function with automatic retry logic
   * @param {Function} fn - Function to wrap
   * @param {Object} options - Retry options
   * @returns {Function} Wrapped function with retry logic
   */
  wrap(fn, options = {}) {
    return async (...args) => {
      return this.execute(() => fn(...args), options);
    };
  }
}

// Create singleton instance for global use
const globalRetryLogic = new RetryLogic();

/**
 * Higher-order function to add retry logic to any async function
 * @param {Function} fn - Function to add retry logic to
 * @param {Object} options - Retry options
 * @returns {Function} Function with retry logic
 */
export const withRetry = (fn, options = {}) => {
  return globalRetryLogic.wrap(fn, options);
};

/**
 * Execute a function with retry logic (imperative approach)
 * @param {Function} fn - Function to execute
 * @param {Object} options - Retry options
 * @returns {Promise} Promise that resolves with the function result
 */
export const executeWithRetry = (fn, options = {}) => {
  return globalRetryLogic.execute(fn, options);
};

export default RetryLogic;